import { useState, useEffect } from "react";
import { X, Smartphone, Star, Download, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.bidzuu.app";

// Smart App Banner - Shows on mobile web browsers
export const SmartAppBanner = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user is on mobile and hasn't dismissed
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const wasDismissed = localStorage.getItem('app_banner_dismissed');
    
    if (isMobile && !wasDismissed) {
      // Show after a small delay
      const timer = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    localStorage.setItem('app_banner_dismissed', 'true');
  };

  const handleOpenApp = () => {
    window.open(PLAY_STORE_URL, '_blank');
  };

  if (!show || dismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-4 py-3 shadow-lg">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
            <img 
              src="https://play-lh.googleusercontent.com/placeholder" 
              alt="BidZuu App"
              className="w-8 h-8 rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden w-8 h-8 bg-emerald-100 rounded-lg items-center justify-center">
              <span className="text-emerald-600 font-bold text-sm">B</span>
            </div>
          </div>
          <div>
            <p className="font-semibold text-sm">BidZuu App</p>
            <div className="flex items-center gap-1 text-xs text-emerald-100">
              <Star className="h-3 w-3 fill-current" />
              <span>Get the best experience</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="secondary"
            onClick={handleOpenApp}
            className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold text-xs px-3"
          >
            Open
          </Button>
          <button 
            onClick={handleDismiss}
            className="p-1 hover:bg-emerald-700/50 rounded-full transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Get App Button for Header
export const GetAppButton = ({ className = "" }) => {
  const [showQR, setShowQR] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowQR(true)}
        className={`text-xs gap-1.5 ${className}`}
        data-testid="get-app-button"
      >
        <Smartphone className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Get App</span>
      </Button>

      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-emerald-600" />
              Get the BidZuu App
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* QR Code Section */}
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="w-48 h-48 mx-auto bg-white rounded-xl shadow-inner flex items-center justify-center mb-4">
                {/* QR Code - Using a QR code generator API */}
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(PLAY_STORE_URL)}`}
                  alt="Scan to download BidZuu app"
                  className="w-44 h-44"
                />
              </div>
              <p className="text-sm text-slate-600">Scan with your phone camera</p>
            </div>

            {/* Direct Download Link */}
            <div className="flex flex-col gap-3">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-black text-white rounded-xl px-6 py-3 hover:bg-slate-800 transition-colors"
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <div className="text-left">
                  <p className="text-[10px] text-slate-400 uppercase">Get it on</p>
                  <p className="font-semibold text-sm">Google Play</p>
                </div>
              </a>

              <p className="text-xs text-center text-slate-500">
                iOS version coming soon!
              </p>
            </div>

            {/* App Features */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Why use the app?</p>
              <ul className="text-xs text-slate-600 space-y-1.5">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Instant notifications for bids & messages
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Faster, smoother browsing experience
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Easy camera access for listing items
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Save data with optimized images
                </li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Homepage App Promotion Banner
export const AppPromoBanner = ({ className = "" }) => {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('app_promo_dismissed');
    if (wasDismissed) setDismissed(true);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('app_promo_dismissed', 'true');
  };

  if (dismissed) return null;

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 p-6 md:p-8 ${className}`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
      </div>

      {/* Dismiss Button */}
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="relative flex flex-col md:flex-row items-center gap-6">
        {/* Phone Mockup */}
        <div className="flex-shrink-0">
          <div className="relative w-32 h-56 md:w-40 md:h-72 bg-slate-900 rounded-3xl p-1.5 shadow-2xl transform -rotate-6 hover:rotate-0 transition-transform duration-300">
            <div className="w-full h-full bg-gradient-to-b from-emerald-400 to-emerald-600 rounded-2xl overflow-hidden flex items-center justify-center">
              <div className="text-center text-white">
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl mx-auto mb-2 flex items-center justify-center">
                  <span className="text-3xl font-bold">B</span>
                </div>
                <p className="text-xs font-medium opacity-90">BidZuu</p>
              </div>
            </div>
            {/* Phone Notch */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-slate-800 rounded-full" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 text-center md:text-left">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-white text-xs font-medium mb-3">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            Now Live on Google Play
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Get the BidZuu App
          </h3>
          <p className="text-emerald-100 text-sm md:text-base mb-4 max-w-md">
            Shop, bid, and sell on the go. Get instant notifications and never miss a deal!
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-3 justify-center md:justify-start">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-black text-white rounded-xl px-5 py-2.5 hover:bg-slate-800 transition-colors shadow-lg"
              data-testid="play-store-button"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              <div className="text-left">
                <p className="text-[9px] text-slate-400 uppercase leading-tight">Get it on</p>
                <p className="font-semibold text-sm leading-tight">Google Play</p>
              </div>
            </a>

            {/* QR Code for Desktop */}
            <div className="hidden lg:flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${encodeURIComponent(PLAY_STORE_URL)}&bgcolor=transparent&color=ffffff`}
                alt="Scan QR"
                className="w-12 h-12"
              />
              <div className="text-white text-xs">
                <p className="font-medium">Scan to download</p>
                <p className="opacity-70">Point camera here</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Footer App Download Section
export const FooterAppSection = () => {
  return (
    <div className="space-y-3">
      <h4 className="font-semibold text-white">Get the App</h4>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg px-4 py-2 transition-colors"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
        </svg>
        <span className="text-sm">Google Play</span>
      </a>
      <p className="text-xs text-slate-400">iOS coming soon</p>
    </div>
  );
};

export default { SmartAppBanner, GetAppButton, AppPromoBanner, FooterAppSection };
